import React, { useCallback, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { CARE_NET_BASE, careNetUrl, careNetUrlWithToken, fetchCareNetSsoToken } from "@/lib/careNet";

/**
 * Anchor to Care Net that upgrades the href to an SSO token on first
 * interaction (hover / focus / click). Signed-out visitors get the clean
 * base URL. Signed-in users get a JWT ?sso=… for zero-typing auth; if the
 * token endpoint fails we fall back to the email hint.
 *
 * Behaves like a normal `<a>` — pass any className / children / data-testid.
 */
export default function CareNetLink({ children, className = "", ...rest }) {
  const { user } = useAuth();
  const [href, setHref] = useState(user ? careNetUrl(user) : CARE_NET_BASE);
  const inFlight = useRef(false);
  const resolvedAt = useRef(0);

  const upgrade = useCallback(async () => {
    if (!user || inFlight.current) return;
    // JWT lasts 5 minutes — refresh if older than 4 min to be safe
    if (Date.now() - resolvedAt.current < 4 * 60 * 1000) return;
    inFlight.current = true;
    const token = await fetchCareNetSsoToken();
    if (token) {
      setHref(careNetUrlWithToken(token));
      resolvedAt.current = Date.now();
    }
    inFlight.current = false;
  }, [user]);

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      onMouseEnter={upgrade}
      onFocus={upgrade}
      onTouchStart={upgrade}
      {...rest}
    >
      {children}
    </a>
  );
}
